import React from 'react';
import { MapPin, Phone, Mail, Shield, Lock } from 'lucide-react';
import type { PublicCompanyProfile } from '@/hooks/useCompanyProfile';

interface ComplianceFooterProps {
  companyProfile: PublicCompanyProfile;
}

export const ComplianceFooter: React.FC<ComplianceFooterProps> = ({ companyProfile }) => {
  const currentYear = new Date().getFullYear();

  const companyName = companyProfile.company_name;
  const legalName = companyProfile.legal_name;
  const nmlsId = companyProfile.company_nmls_id;
  const mainPhone = companyProfile.main_phone;
  const mainEmail = companyProfile.main_email;
  const equalHousingLender = companyProfile.equal_housing_lender;

  const address = companyProfile.headquarters_address;

  const stateLicenses = companyProfile.state_licenses;
  const licensedStates = stateLicenses
    .map((license) => `${license.state} (${license.license_number})`)
    .join(', ');

  return (
    <div className="relative bg-gradient-to-br from-cyan-50/30 via-white to-blue-50/30 border-t border-cyan-200/30 mt-16">
      {/* Decorative gradient accent */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-8 py-12">
        {/* Trust Badges Section */}
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          {/* SSL/TLS Badge */}
          <div className="group relative glass-card px-6 py-4 hover:shadow-cyan transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-green-400/20 rounded-lg blur-sm" />
                <Lock className="relative h-6 w-6 text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-xs font-bold text-gray-900 whitespace-nowrap">
                256-bit SSL/TLS Encrypted
              </div>
            </div>
          </div>

          {/* NMLS Badge */}
          <div className="group relative glass-card px-6 py-4 hover:shadow-cyan transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-400/20 rounded-lg blur-sm" />
                <Shield className="relative h-6 w-6 text-blue-600 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-xs font-bold text-gray-900 whitespace-nowrap">
                NMLS# {nmlsId} Licensed Lender
              </div>
            </div>
          </div>

          {/* Equal Housing Badge */}
          {equalHousingLender && (
            <div className="group relative glass-card px-6 py-4 hover:shadow-cyan transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="text-2xl">🏠</div>
                <div className="text-xs font-bold text-gray-900 whitespace-nowrap">
                  Equal Housing Lender
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Company Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          {/* Company Name & Tagline */}
          <div>
            <div className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-3">
              {legalName}
            </div>
            <div className="text-sm text-gray-700 mb-5 font-medium">
              Professional Lending Solutions
            </div>
            <div className="text-xs text-gray-600">
              <p>NMLS# {nmlsId}</p>
            </div>
          </div>

          {/* Headquarters Address */}
          <div>
            <div className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-600" />
              Headquarters
            </div>
            <div className="text-sm text-gray-700 space-y-1">
              <p>{address.street}{address.suite ? `, Suite ${address.suite}` : ''}</p>
              <p>{address.city}, {address.state} {address.zip}</p>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <div className="text-sm font-bold text-gray-800 mb-4">
              Contact Us
            </div>
            <div className="text-sm text-gray-700 space-y-2">
              <a
                href={`tel:${mainPhone.replace(/\D/g, '')}`}
                className="group flex items-center gap-2 hover:text-cyan-600 transition-all duration-300"
              >
                <Phone className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                <span className="font-medium">{mainPhone}</span>
              </a>
              <a
                href={`mailto:${mainEmail}`}
                className="group flex items-center gap-2 hover:text-cyan-600 transition-all duration-300"
              >
                <Mail className="w-4 h-4 group-hover:-rotate-12 transition-transform" />
                <span className="font-medium">{mainEmail}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Legal & Compliance Section */}
        <div className="border-t border-cyan-200/30 pt-8">
          <div className="text-center space-y-4">
            {/* Equal Housing & NMLS */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-700">
              <span className="font-bold">NMLS# {nmlsId}</span>
              <span className="text-cyan-400">•</span>
              {equalHousingLender && (
                <>
                  <span className="font-bold">Equal Housing Lender</span>
                  <span className="text-cyan-400">•</span>
                </>
              )}
              <span>Licensed in {licensedStates}</span>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
              <a
                href="/privacy-policy"
                className="text-cyan-600 hover:text-blue-600 transition-colors underline font-medium"
              >
                Privacy Policy
              </a>
              <span className="text-gray-300">•</span>
              <a
                href="/terms-of-service"
                className="text-cyan-600 hover:text-blue-600 transition-colors underline font-medium"
              >
                Terms of Service
              </a>
              <span className="text-gray-300">•</span>
              <a
                href="/accessibility"
                className="text-cyan-600 hover:text-blue-600 transition-colors underline font-medium"
              >
                Accessibility
              </a>
              <span className="text-gray-300">•</span>
              <a
                href="/licensing"
                className="text-cyan-600 hover:text-blue-600 transition-colors underline font-medium"
              >
                State Licensing
              </a>
            </div>

            {/* Security & Disclaimer */}
            <div className="relative glass-card text-xs text-gray-700 max-w-3xl mx-auto mt-6 p-6">
              <div className="flex items-start gap-3 mb-3">
                <Lock className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-left">
                  All documents are transmitted using 256-bit SSL/TLS encryption and stored securely in compliance with federal privacy regulations.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="h-4 w-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                <p className="text-left">
                  This portal is for authorized personnel and applicants only. By uploading documents, you acknowledge that you have read and agree to our Privacy Policy and Terms of Service.
                </p>
              </div>
              <div className="mt-4 border-t border-cyan-200/30 pt-4 text-left space-y-1">
                {companyProfile.default_disclaimers.map((disclaimer) => (
                  <p key={disclaimer}>{disclaimer}</p>
                ))}
              </div>
            </div>

            {/* Copyright */}
            <div className="text-xs text-gray-500 mt-6 font-medium">
              © {currentYear} {companyName}. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
